import { Button, Icon, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@nexus/ui";
import { useTranslation } from "react-i18next";

export interface PersonaOpportunityBulkActionBarProps {
  selectedCount: number;
  isGenerating: boolean;
  canGenerateScripts: boolean;
  onClearSelection: () => void;
  onGenerateScripts: () => void;
}

/**
 * Persona Opportunities bulk bar — Generate Scripts + Clear only (no Reject).
 * Pattern reference: legacy `BulkActionBar`; do not import it (R24 / KTD14).
 */
export function PersonaOpportunityBulkActionBar({
  selectedCount,
  isGenerating,
  canGenerateScripts,
  onClearSelection,
  onGenerateScripts,
}: PersonaOpportunityBulkActionBarProps) {
  const { t } = useTranslation();

  if (selectedCount <= 0) return null;

  const selectedLabel =
    selectedCount === 1
      ? t("personaOpportunities.bulk.selected", "1 selected")
      : t("personaOpportunities.bulk.selectedPlural", {
          defaultValue: "{{count}} selected",
          count: selectedCount,
        });

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-40 flex justify-center px-3 pb-[max(1rem,env(safe-area-inset-bottom))] animate-in fade-in slide-in-from-bottom-2 duration-300 sm:bottom-4 sm:px-4 sm:pb-0"
      role="region"
      aria-label={t("personaOpportunities.bulk.barAria", {
        defaultValue: "Bulk actions for {{count}} selected opportunities",
        count: selectedCount,
      })}
      data-testid="persona-opportunity-bulk-action-bar"
    >
      <div className="flex w-full max-w-lg flex-col gap-2 rounded-md border border-border bg-background px-3 py-3 shadow-[0_1px_3px_0_rgba(0,0,0,0.1),0_1px_2px_-1px_rgba(0,0,0,0.1)] sm:w-auto sm:max-w-xl sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-3 sm:gap-y-0 sm:px-4 sm:py-2 dark:bg-muted">
        <div className="flex w-full min-w-0 items-center justify-between gap-2 sm:w-auto sm:justify-start">
          <div className="flex min-w-0 items-center gap-2">
            <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary text-[11px] font-medium tabular-nums text-primary-foreground">
              {selectedCount}
            </span>
            <span className="truncate text-sm font-medium text-foreground">{selectedLabel}</span>
          </div>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={onClearSelection}
                  className="inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background [@media(hover:hover)_and_(pointer:fine)]:min-h-7 [@media(hover:hover)_and_(pointer:fine)]:min-w-7"
                  aria-label={t("personaOpportunities.bulk.clearSelection", "Clear selection")}
                >
                  <Icon name="x" size="xs" />
                </button>
              </TooltipTrigger>
              <TooltipContent>
                {t("personaOpportunities.bulk.clearSelection", "Clear selection")}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        {canGenerateScripts ? (
          <Button
            type="button"
            variant="default"
            size="sm"
            iconStart={isGenerating ? undefined : "sparkles"}
            disabled={isGenerating}
            onClick={onGenerateScripts}
            className="min-h-11 w-full text-xs [@media(hover:hover)_and_(pointer:fine)]:min-h-8 sm:w-auto"
            data-testid="persona-bulk-generate-scripts"
          >
            {isGenerating ? (
              <>
                <Icon name="loader" size="xs" className="animate-spin shrink-0" aria-hidden />
                <span className="truncate">
                  {t("personaOpportunities.bulk.generatingScripts", "Generating scripts…")}
                </span>
              </>
            ) : (
              t("personaOpportunities.bulk.generateScripts", "Generate Scripts")
            )}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
